/**
 * Connection pane for the right sidebar - shows connection details and settings
 * Displays connection configuration, capabilities, triggers, and management options
 * Uses the compositional PaneLayout pattern for consistent pane layout
 */

import { ConnectionContent } from "@/components/ConnectionContent/ConnectionContent";
import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { DropZoneProvider } from "@/dnd/OsFileDropLayer";
import { useAppNavigate, usePaneParams } from "@/hooks/pane-routing";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { CANVAS_DETAIL_PANE_HEADER_CLASS } from "@/panes/canvasDetailPaneHeaderClasses";
import { route } from "@/utils/route";
import {
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutEntityHeader,
} from "@cohesive/ui-components";
import { Maximize2, Minimize2, X } from "lucide-react";

export const ConnectionPane = () => {
  const params = usePaneParams(ROUTES.SETTINGS.INTEGRATION_SETTINGS);
  const integrationId = params.integrationId;
  const openSettings = useOpenSettings();
  const appNavigate = useAppNavigate();
  const lg = useBreakpoint("lg");
  const paneContext = usePaneRouterContextSafe();

  return (
    <DropZoneProvider>
      <PaneLayout>
        {/* Simple header bar with navigation controls */}
        <PaneLayoutEntityHeader
          className={`${CANVAS_DETAIL_PANE_HEADER_CLASS} flex items-center justify-end gap-1`}
        >
          <TooltipProvider>
            {/* Expand/Minimize */}
            {lg && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    onClick={() => {
                      if (paneContext) {
                        openSettings(
                          route(ROUTES.SETTINGS.INTEGRATION_SETTINGS, {
                            integrationId,
                          }),
                        );
                        appNavigate({ pane: "rightPane", to: null });
                      } else {
                        openSettings(
                          route(ROUTES.SETTINGS.INTEGRATION_SETTINGS, {
                            integrationId,
                          }),
                        );
                      }
                    }}
                  >
                    {paneContext ? <Maximize2 /> : <Minimize2 />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {paneContext ? "Open in settings" : "Close settings"}
                </TooltipContent>
              </Tooltip>
            )}

            {/* Close */}
            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    onClick={() => appNavigate({ pane: "rightPane", to: null })}
                    aria-label="Close"
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutEntityHeader>
        <PaneLayoutContent>
          <ConnectionContent integrationId={integrationId} context="pane" />
        </PaneLayoutContent>
      </PaneLayout>
    </DropZoneProvider>
  );
};
